import { useState } from 'react'
import { useSaveNotes } from '../../api/queries/dealMutations'
import { userFacingMessage } from '../../api/errors'
import { Button } from '../../ui/Button'
import { TextArea } from '../../ui/TextArea'
import { Prose } from './Prose'
import styles from './DealPanel.module.css'

/**
 * The user's private notes on a deal.
 *
 * Edit / save-when-dirty / cancel, as in the approved design. The draft lives in
 * component state only while the form is open, which is local UI state rather
 * than business state (docs/16 section 7.3): cancel discards it, and a reload
 * shows what the server holds.
 */
export function NotesEditor({ dealId, notes }: { dealId: string; notes: string | null }) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState('')
  const save = useSaveNotes(dealId)

  function open() {
    setDraft(notes ?? '')
    save.reset()
    setEditing(true)
  }

  function submit() {
    save.mutate(draft, { onSuccess: () => setEditing(false) })
  }

  return (
    <section>
      <h3 className={styles.section}>Your notes</h3>

      {editing ? (
        <>
          <TextArea
            label="Your notes"
            hideLabel
            autoFocus
            value={draft}
            maxLength={5000}
            placeholder="Private notes on this deal — only you see these."
            onChange={(event) => setDraft(event.target.value)}
          />
          <div className={styles.editActions}>
            <Button variant="primary" onClick={submit} disabled={save.isPending}>
              {save.isPending ? 'Saving…' : 'Save'}
            </Button>
            <Button onClick={() => setEditing(false)} disabled={save.isPending}>
              Cancel
            </Button>
          </div>
          {save.isError ? (
            <p className={styles.failed} role="alert">
              Your notes were not saved. {userFacingMessage(save.error)}
            </p>
          ) : null}
        </>
      ) : (
        <>
          {notes?.trim() ? <Prose text={notes} /> : <p className={styles.absent}>No notes yet.</p>}
          <div className={styles.editActions}>
            <Button onClick={open}>{notes?.trim() ? 'Edit notes' : 'Add notes'}</Button>
          </div>
        </>
      )}
    </section>
  )
}
